import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  getProfile,
  updateProfile,
  getLogoPresignedUrl,
  uploadLogoToS3,
} from "@/api/profile";
import { useAuthStore } from "@/stores/authStore";

const schema = z.object({
  company_name: z.string().max(255, "Max 255 characters").optional(),
  contact_email: z.email("Invalid email address").optional().or(z.literal("")),
  phone: z.string().max(50, "Max 50 characters").optional(),
  company_website: z
    .string()
    .refine(
      (val) =>
        !val || /^https?:\/\/.+/.test(val) || /^[\w-]+(\.[\w-]+)+/.test(val),
      "Please enter a valid website (e.g., company.com or https://company.com)",
    )
    .optional(),
});

const normalizeWebsite = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
};

type FormData = z.infer<typeof schema>;

export function BuilderSettings() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setProfileSetupRequired = useAuthStore(
    (s) => s.setProfileSetupRequired,
  );
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const { data: profile, isLoading } = useQuery({
    queryKey: ["profile"],
    queryFn: getProfile,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (profile) {
      reset({
        company_name: profile.company_name ?? "",
        contact_email: profile.contact_email ?? "",
        phone: profile.phone ?? "",
        company_website: profile.company_website ?? "",
      });
      if (profile.logo_url) {
        setLogoPreview(profile.logo_url);
      }
    }
  }, [profile, reset]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const onSubmit = async (data: FormData) => {
    setSaving(true);
    try {
      let logoS3Key: string | undefined;

      if (logoFile) {
        const presign = await getLogoPresignedUrl(logoFile.name, logoFile.type);
        await uploadLogoToS3(presign.upload_url, logoFile);
        logoS3Key = presign.s3_key;
      }

      await updateProfile({
        company_name: data.company_name,
        contact_email: data.contact_email || undefined,
        phone: data.phone || undefined,
        company_website: normalizeWebsite(data.company_website || undefined),
        ...(logoS3Key ? { logo_s3_key: logoS3Key } : {}),
      });

      queryClient.invalidateQueries({ queryKey: ["profile"] });
      setLogoFile(null);
      if (data.company_name) {
        setProfileSetupRequired(false);
      }
      toast.success("Settings saved");
      navigate("/");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Save failed";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <p className="text-center text-muted-foreground">Loading...</p>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Card className="mx-auto max-w-lg">
        <CardHeader>
          <CardTitle>Builder Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name</Label>
              <Input id="company_name" {...register("company_name")} />
              {errors.company_name && (
                <p className="text-sm text-destructive">
                  {errors.company_name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_email">Contact email</Label>
              <Input
                id="contact_email"
                type="email"
                placeholder="contact@company.com"
                {...register("contact_email")}
              />
              {errors.contact_email && (
                <p className="text-sm text-destructive">
                  {errors.contact_email.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                {...register("phone")}
              />
              {errors.phone && (
                <p className="text-sm text-destructive">
                  {errors.phone.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_website">Company Website</Label>
              <Input
                id="company_website"
                type="text"
                placeholder="company.com"
                {...register("company_website")}
              />
              <p className="text-xs text-muted-foreground">
                If you don't include the beginning of the URL, it will default
                to <span className="font-medium">https://</span>. You can also
                enter a full URL (e.g., http://company.com).
              </p>
              {errors.company_website && (
                <p className="text-sm text-destructive">
                  {errors.company_website.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Company Logo</Label>
              {logoPreview && (
                <div className="mb-2">
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="h-20 w-20 rounded-md border object-contain"
                  />
                </div>
              )}
              <Input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/svg+xml"
                className="hover:cursor-pointer"
                onChange={handleLogoChange}
              />
            </div>

            <Button
              type="submit"
              className="w-full border rounded-xl hover:cursor-pointer"
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
